import axios from 'axios';

/**
 * RAG (Retrieval-Augmented Generation) utility functions for the Voice Chatbot application.
 * Handles document management and vector store operations.
 */

/**
 * Load RAG documents for a specific chatbot
 * @param {string} selectedPromptTitle - The chatbot ID/title
 * @param {boolean} isEditMode - Whether in edit mode
 * @param {Function} setRagDocuments - State setter for documents
 * @returns {Promise<void>}
 */
export const loadRagDocuments = async (selectedPromptTitle, isEditMode, setRagDocuments) => {
    if (!isEditMode || !selectedPromptTitle) {
        setRagDocuments([]);
        return;
    }
    
    try {
            const response = await axios.get(`/api/chatbot/${encodeURIComponent(selectedPromptTitle)}/documents`);
        setRagDocuments(response.data.documents || []);
    } catch (error) {
        console.error('Error loading RAG documents:', error);
        setRagDocuments([]);
    }
};

/**
 * Handle file upload to chatbot's RAG vector store
 * @param {Event} event - File input change event
 * @param {string} selectedPromptTitle - The chatbot ID/title
 * @param {Function} setRagUploadLoading - State setter for upload loading
 * @param {Function} setStatusMessage - State setter for status messages
 * @param {Function} loadDocuments - Function to reload documents after upload
 * @returns {Promise<void>}
 */
export const handleRagFileUpload = async (
    event,
    selectedPromptTitle,
    setRagUploadLoading,
    setStatusMessage,
    loadDocuments,
    setOcrStatusMessage,
    setRagUploadCancelHandler
) => {
    if (!selectedPromptTitle) {
        setStatusMessage('Please save the chatbot first before uploading documents.');
        return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    setRagUploadLoading(true);
    if (typeof setOcrStatusMessage === 'function') {
        setOcrStatusMessage('');
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('chatbot_name', selectedPromptTitle);
    const uploadController = new AbortController();
    let ocrStatusTimer = null;
    let stopOcrPolling = false;
    let cancellationRequested = false;

    const requestCancel = async () => {
        cancellationRequested = true;
        stopOcrPolling = true;
        if (ocrStatusTimer) {
            clearTimeout(ocrStatusTimer);
        }

        try {
            await axios.post(`/api/chatbot/${encodeURIComponent(selectedPromptTitle)}/cancel_ocr`);
        } catch (error) {
            // Best effort cancel request.
        }

        uploadController.abort();
        setStatusMessage('OCR canceled.');
    };

    if (typeof setRagUploadCancelHandler === 'function') {
        setRagUploadCancelHandler(() => requestCancel);
    }

    if (typeof setOcrStatusMessage === 'function') {
        const pollOcrStatus = async () => {
            if (stopOcrPolling) return;

            let nextDelayMs = 2500; // Backoff while OCR is not active.
            try {
                const statusResponse = await axios.get(
                    `/api/chatbot/${encodeURIComponent(selectedPromptTitle)}/ocr_status`
                );
                const status = statusResponse?.data || {};
                if (status.ocr_in_progress && status.current_page) {
                    const suffix = status.total_pages ? `/${status.total_pages}` : '';
                    setOcrStatusMessage(`OCR page: ${status.current_page}${suffix}`);
                    nextDelayMs = 500; // Frequent updates only while OCR is active.
                } else {
                    setOcrStatusMessage('');
                }
            } catch (error) {
                // Ignore transient polling errors during upload.
            }

            if (!stopOcrPolling) {
                ocrStatusTimer = setTimeout(pollOcrStatus, nextDelayMs);
            }
        };

        pollOcrStatus();
    }

    try {
        const response = await axios.post(
            `/api/chatbot/${encodeURIComponent(selectedPromptTitle)}/upload_document`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                signal: uploadController.signal
            }
        );
        
        if (response.data.success) {
            setStatusMessage(`Document uploaded: ${file.name}`);
            await loadDocuments();
        } else {
            setStatusMessage(`Error: ${response.data.message || 'Upload failed'}`);
        }
    } catch (error) {
        if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            if (!cancellationRequested) {
                setStatusMessage('OCR canceled.');
            }
            return;
        }

        console.error('Error uploading RAG document:', error);
        if (error.response?.status === 499 || error.response?.data?.canceled) {
            setStatusMessage('OCR canceled.');
            return;
        }

        const backendError = error.response?.data?.error || error.message || '';
        if (backendError.includes('Expected Embeddings to be non-empty list or numpy array, got [] in upsert')) {
            setStatusMessage('Error: Document is empty, please check your file again before reuploading.');
        } else {
            setStatusMessage(`Error uploading document: ${backendError}`);
        }
    } finally {
        stopOcrPolling = true;
        if (ocrStatusTimer) {
            clearTimeout(ocrStatusTimer);
        }
        if (typeof setOcrStatusMessage === 'function') {
            setOcrStatusMessage('');
        }
        if (typeof setRagUploadCancelHandler === 'function') {
            setRagUploadCancelHandler(null);
        }
        setRagUploadLoading(false);
        event.target.value = '';
    }
};

/**
 * Handle URL submission to chatbot's RAG vector store
 * @param {string} ragUploadUrl - The URL to process
 * @param {string} selectedPromptTitle - The chatbot ID/title
 * @param {Function} setRagUrlLoading - State setter for URL loading
 * @param {Function} setStatusMessage - State setter for status messages
 * @param {Function} setRagUploadUrl - State setter to clear input
 * @param {Function} loadDocuments - Function to reload documents after processing
 * @returns {Promise<void>}
 */
export const handleRagUrlSubmit = async (
    ragUploadUrl,
    selectedPromptTitle,
    setRagUrlLoading,
    setStatusMessage,
    setRagUploadUrl,
    loadDocuments,
    onUrlSuccess
) => {
    if (!ragUploadUrl.trim()) {
        setStatusMessage('Please enter a URL');
        return;
    }

    if (!selectedPromptTitle) {
        setStatusMessage('Please save the chatbot first before adding URLs.');
        return;
    }

    setRagUrlLoading(true);
    try {
        const response = await axios.post(
            `/api/chatbot/${encodeURIComponent(selectedPromptTitle)}/add_url`,
            {
                url: ragUploadUrl,
                chatbot_name: selectedPromptTitle
            }
        );

        if (response.data.success) {
            setStatusMessage(`URL processed successfully`);
            setRagUploadUrl('');
            let shouldReloadCurrentView = true;
            if (typeof onUrlSuccess === 'function') {
                const callbackResult = await onUrlSuccess(response.data);
                shouldReloadCurrentView = callbackResult !== false;
            }
            if (shouldReloadCurrentView) {
                await loadDocuments();
            }
        } else {
            setStatusMessage(`Error: ${response.data.message || 'Processing failed'}`);
        }
    } catch (error) {
        console.error('Error processing RAG URL:', error);
        setStatusMessage(`Error processing URL: ${error.response?.data?.error || error.message}`);
    } finally {
        setRagUrlLoading(false);
    }
};

/**
 * Clear all documents from a chatbot's vector store
 * @param {string} selectedPromptTitle - The chatbot ID/title
 * @param {Function} setStatusMessage - State setter for status messages
 * @param {Function} loadDocuments - Function to reload documents after clearing
 * @returns {Promise<void>}
 */
export const handleClearRagDocuments = async (
    selectedPromptTitle,
    setStatusMessage,
    loadDocuments
) => {
    if (!selectedPromptTitle) return;

    try {
        await axios.post(`/api/chatbot/${encodeURIComponent(selectedPromptTitle)}/clear_documents`);
        setStatusMessage('All documents cleared');
        await loadDocuments();
    } catch (error) {
        console.error('Error clearing RAG documents:', error);
        setStatusMessage(`Error clearing documents: ${error.response?.data?.error || error.message}`);
    }
};

/**
 * Handle RAG toggle - opens file upload dialog when RAG is enabled
 * @param {boolean} isEnabled - Whether RAG is being enabled or disabled
 * @param {string} selectedPromptTitle - The chatbot ID/title
 * @param {React.MutableRefObject} fileInputRef - Reference to hidden file input element
 * @param {Function} setStatusMessage - State setter for status messages
 * @param {Object} formData - Current form data to preserve other settings
 * @returns {Promise<void>}
 */
export const handleRagToggle = async (isEnabled, selectedPromptTitle, fileInputRef, setStatusMessage, formData) => {
    if (isEnabled) {
        // Update backend immediately when RAG is enabled
        if (!selectedPromptTitle) {
            // Disable toggle if chatbot is not saved yet
            setStatusMessage('Please save the chatbot first before enabling RAG.');
            return;
        }
        if (formData) {
            try {
                await axios.post('/api/update_prompt', {
                    title: selectedPromptTitle,
                    name: formData.name || '',
                    language: formData.language || '',
                    level: formData.level || '',
                    voice: formData.voice || 'alloy',
                    initialText: formData.initialText || '',
                    prompt: formData.prompt || '',
                    instructions: formData.instructions || '',
                    avatar_url: formData.avatar_url || '',
                    use_rag: true
                });
                setStatusMessage('RAG enabled for this chatbot.');
            } catch (error) {
                console.error('Error enabling RAG:', error);
                setStatusMessage('Error enabling RAG. Please try again.');
            }
        }
    } else {
        // Update backend immediately when RAG is disabled
        if (selectedPromptTitle && formData) {
            try {
                await axios.post('/api/update_prompt', {
                    title: selectedPromptTitle,
                    name: formData.name || '',
                    language: formData.language || '',
                    level: formData.level || '',
                    voice: formData.voice || 'alloy',
                    initialText: formData.initialText || '',
                    prompt: formData.prompt || '',
                    instructions: formData.instructions || '',
                    avatar_url: formData.avatar_url || '',
                    use_rag: false
                });
                setStatusMessage('RAG disabled for this chatbot.');
            } catch (error) {
                console.error('Error disabling RAG:', error);
                setStatusMessage('Error disabling RAG. Please try again.');
            }
        } else {
            setStatusMessage('RAG disabled for this chatbot.');
        }
    }
};

/**
 * Format URL to human-readable form by decoding URL-encoded characters
 * @param {string} url - The URL to format
 * @returns {string} - Human-readable URL
 */
export const formatUrlForDisplay = (url) => {
    if (!url) return '';
    
    try {
        // Decode URL-encoded characters for better readability
        return decodeURIComponent(url);
    } catch (error) {
        // If decoding fails, return original URL
        console.warn('Error decoding URL:', error);
        return url;
    }
};

export const createRagUrlSuccessHandler = ({
    uploadTargetPrompt,
    selectedPromptTitleRef,
    isEditModeRef,
    setRagDocuments
}) => {
    return async (responseData) => {
        const isStillActivePrompt =
            selectedPromptTitleRef.current === uploadTargetPrompt &&
            isEditModeRef.current;

        if (!isStillActivePrompt) {
            return false;
        }

        if (Array.isArray(responseData.documents_processed)) {
            setRagDocuments(responseData.documents_processed);
            return false;
        }

        return true;
    };
};

export const submitRagUrlForCurrentPrompt = async ({
    ragUploadUrl,
    selectedPromptTitle,
    setRagUrlLoading,
    setStatusMessage,
    setRagUploadUrl,
    loadDocuments,
    selectedPromptTitleRef,
    isEditModeRef,
    setRagDocuments
}) => {
    const uploadTargetPrompt = selectedPromptTitle;
    await handleRagUrlSubmit(
        ragUploadUrl,
        uploadTargetPrompt,
        setRagUrlLoading,
        setStatusMessage,
        setRagUploadUrl,
        loadDocuments,
        createRagUrlSuccessHandler({
            uploadTargetPrompt,
            selectedPromptTitleRef,
            isEditModeRef,
            setRagDocuments
        })
    );
};

export const openClearRagDocumentsDialog = (setOpenClearDocsDialog) => {
    setOpenClearDocsDialog(true);
};

export const confirmClearRagDocuments = async ({
    selectedPromptTitle,
    setStatusMessage,
    loadRagDocuments,
    setOpenClearDocsDialog
}) => {
    setOpenClearDocsDialog(false);
    await handleClearRagDocuments(
        selectedPromptTitle,
        setStatusMessage,
        loadRagDocuments
    );
};

export const cancelClearRagDocuments = (setOpenClearDocsDialog) => {
    setOpenClearDocsDialog(false);
};

export const openDeleteRagDocumentDialog = ({
    storedFilename,
    displayFilename,
    setDocToDelete,
    setOpenDeleteDocDialog
}) => {
    setDocToDelete({ storedFilename, displayFilename });
    setOpenDeleteDocDialog(true);
};

export const confirmDeleteRagDocument = async ({
    docToDelete,
    selectedPromptTitle,
    setOpenDeleteDocDialog,
    setDeletingDocFilename,
    setRagDocuments,
    loadRagDocuments,
    setStatusMessage,
    setDocToDelete
}) => {
    setOpenDeleteDocDialog(false);
    if (!docToDelete) return;

    setDeletingDocFilename(docToDelete.storedFilename);

    try {
        const response = await axios.post(`/api/chatbot/${selectedPromptTitle}/delete_document`, {
            filename: docToDelete.storedFilename || docToDelete.displayFilename
        });

        if (response.data.success && response.data.remaining_documents !== undefined) {
            setRagDocuments(response.data.remaining_documents);
        } else {
            await loadRagDocuments();
        }

        setStatusMessage(`Document "${docToDelete.displayFilename}" deleted successfully`);
    } catch (error) {
        console.error('Error deleting document:', error);
        setStatusMessage(`Error deleting document: ${error.response?.data?.error || error.message}`);
    } finally {
        setDocToDelete(null);
        setDeletingDocFilename(null);
    }
};

export const cancelDeleteRagDocument = ({
    setOpenDeleteDocDialog,
    setDocToDelete
}) => {
    setOpenDeleteDocDialog(false);
    setDocToDelete(null);
};

export const handleDownloadOcrDocument = async ({
    selectedPromptTitle,
    documentId,
    displayFilename,
    setDownloadingOcrFilename,
    setStatusMessage
}) => {
    if (!selectedPromptTitle || !documentId) return;

    setDownloadingOcrFilename(documentId);
    try {
        const response = await axios.post(
            `/api/chatbot/${encodeURIComponent(selectedPromptTitle)}/download_ocr_document`,
            { filename: documentId },
            { responseType: 'blob' }
        );

        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/plain;charset=utf-8' }));
        const link = document.createElement('a');
        const safeBase = (displayFilename || 'document').replace(/\.[^/.]+$/, '');
        link.href = blobUrl;
        link.setAttribute('download', `${safeBase}_ocr.txt`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);

        setStatusMessage(`Downloaded OCR text for "${displayFilename}"`);
    } catch (error) {
        console.error('Error downloading OCR text:', error);
        let backendErrorMessage = '';
        try {
            const responseData = error?.response?.data;
            if (responseData instanceof Blob) {
                const text = await responseData.text();
                const parsed = JSON.parse(text);
                backendErrorMessage = parsed?.error || '';
            } else if (responseData && typeof responseData === 'object') {
                backendErrorMessage = responseData.error || '';
            }
        } catch (_parseError) {
            // Ignore parse errors and use fallback message.
        }

        setStatusMessage(`Error downloading OCR text: ${backendErrorMessage || error.message}`);
    } finally {
        setDownloadingOcrFilename(null);
    }
};
